import { useCallback, useEffect, useRef } from 'react';

/**
 * L'ancrage du fil en bas — audit UI/UX du 2026-09-23.
 *
 * 1. **À l'ouverture**, une seule fois par conversation, dès qu'il y a un élément.
 * 2. **À l'arrivée d'un nouveau dernier élément**, seulement si l'on était déjà en
 *    bas : quelqu'un qui a remonté le fil pour relire n'est pas ramené de force.
 * 3. **Après un envoi**, toujours : `suivreProchainAjout()` arme le suivi.
 *
 * On suit la CLÉ du dernier élément, pas le nombre : `/get-messages` est plafonné à
 * 50 (`MessageController.php:65`), un nouveau message y chasse le plus ancien et le
 * compte ne bouge pas.
 *
 * `identite` (l'id du client) remet tout à zéro : la route `:id` garde la même
 * instance de `FilConversation` d'une conversation à l'autre.
 *
 * « Être en bas » se lit sur la visibilité d'une sentinelle de fin de fil.
 *
 * 4. **À l'insertion au milieu du fil** (audit 2026-09-23) : les cartes de contexte
 *    (réservations, formulaires, commandes) arrivent APRÈS les messages et se
 *    rangent par date, donc au milieu. La clé du dernier élément ne bouge pas,
 *    mais le dernier message passe sous l'écran. On suit donc AUSSI le nombre
 *    d'éléments : s'il change, on redéfile si l'on était en bas, ou si l'ancrage
 *    initial date de moins de `FENETRE_ARRIVEE_MS` — la sentinelle n'a pas encore
 *    forcément été vue, et c'est justement le moment où les sources de contexte
 *    répondent. Passé ce délai, quelqu'un qui a remonté le fil n'est jamais ramené.
 */
const FENETRE_ARRIVEE_MS = 1500;

export function useAncrageBas(
    derniereCle: string | undefined,
    pret: boolean,
    identite: string | undefined,
    nombre: number,
) {
    const refFin = useRef<HTMLDivElement>(null);
    const dejaAncre = useRef(false);
    const enBas = useRef(false);
    const suivre = useRef(false);
    const clePrecedente = useRef<string | undefined>(undefined);
    const nombrePrecedent = useRef(0);
    const instantAncrage = useRef(0);

    useEffect(() => {
        dejaAncre.current = false;
        suivre.current = false;
        clePrecedente.current = undefined;
        nombrePrecedent.current = 0;
    }, [identite]);

    useEffect(() => {
        const cible = refFin.current;
        if (!cible || typeof IntersectionObserver === 'undefined') return;
        const observateur = new IntersectionObserver((entrees) => {
            enBas.current = entrees.some((e) => e.isIntersecting);
        });
        observateur.observe(cible);
        return () => observateur.disconnect();
    }, []);

    useEffect(() => {
        const defiler = () => refFin.current?.scrollIntoView?.({ block: 'end' });

        if (!pret || !derniereCle) return;

        if (!dejaAncre.current) {
            dejaAncre.current = true;
            instantAncrage.current = Date.now();
            defiler();
        } else if (derniereCle !== clePrecedente.current && (enBas.current || suivre.current)) {
            suivre.current = false;
            defiler();
        } else if (
            nombre !== nombrePrecedent.current &&
            (enBas.current || Date.now() - instantAncrage.current < FENETRE_ARRIVEE_MS)
        ) {
            defiler();
        }
        clePrecedente.current = derniereCle;
        nombrePrecedent.current = nombre;
    }, [derniereCle, pret, identite, nombre]);

    const suivreProchainAjout = useCallback(() => {
        suivre.current = true;
    }, []);

    return { refFin, suivreProchainAjout };
}
